import React, { useEffect, useMemo, useState } from 'react';

const easeOutCubic = (t) => 1 - (1 - t) ** 3;

const toNonNegativeInt = (value) => {
  if (!Number.isFinite(Number(value))) return 0;
  return Math.max(0, Math.round(Number(value)));
};

const getAnimationDuration = (target) => {
  if (target >= 300) return 1100;
  if (target >= 100) return 900;
  return 700;
};

const useCountUp = (target, shouldAnimate) => {
  const [value, setValue] = useState(target);

  useEffect(() => {
    if (!shouldAnimate) {
      setValue(target);
      return undefined;
    }

    if (target <= 0) {
      setValue(0);
      return undefined;
    }

    const startTime =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();
    const duration = getAnimationDuration(target);
    let frameId = null;

    setValue(0);

    const tick = (nowRaw) => {
      const now =
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? nowRaw
          : Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(progress);
      setValue(Math.round(target * eased));
      if (progress < 1) {
        frameId = window.requestAnimationFrame(tick);
      }
    };

    frameId = window.requestAnimationFrame(tick);
    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [target, shouldAnimate]);

  return value;
};

const RewardSummary = ({
  rewardCoins = null,
  pointsAwarded = 0,
  firstGameBonus = false,
  showSignInHint = false,
}) => {
  const hasCoinReward = typeof rewardCoins === 'number';
  const coinTarget = useMemo(
    () => (hasCoinReward ? toNonNegativeInt(rewardCoins) : 0),
    [hasCoinReward, rewardCoins]
  );
  const pointTarget = useMemo(() => toNonNegativeInt(pointsAwarded), [pointsAwarded]);
  const hasPointReward = pointTarget > 0;
  const hasRewards = hasCoinReward || hasPointReward;

  const animatedCoins = useCountUp(coinTarget, hasCoinReward);
  const animatedPoints = useCountUp(pointTarget, hasPointReward);

  if (!hasRewards && !showSignInHint) return null;

  return (
    <div className="reward-summary" aria-live="polite">
      {hasRewards && (
        <div className="reward-summary__grid">
          {hasCoinReward && (
            <div className="reward-summary__pill reward-summary__pill--coins">
              <span className="reward-summary__label">Coins</span>
              <span className="reward-summary__value reward-summary__value--coins">
                +{animatedCoins}
              </span>
            </div>
          )}
          {hasPointReward && (
            <div className="reward-summary__pill reward-summary__pill--points">
              <span className="reward-summary__label">Points</span>
              <span className="reward-summary__value reward-summary__value--points">
                +{animatedPoints}
              </span>
            </div>
          )}
        </div>
      )}

      {firstGameBonus && hasPointReward && (
        <p className="reward-summary__bonus">Includes +15 first game bonus</p>
      )}

      {showSignInHint && (
        <p className="reward-summary__note">Sign in to save coin rewards.</p>
      )}
    </div>
  );
};

export default RewardSummary;
